<template>
  <div class="monetary-dashboard" :class="{ 'monetary-dashboard--dark': isDarkSkin }">

    <div class="monetary-dashboard__toolbar">
      <filter-card :filter-key="viewKey" dashboard single-row v-model="filter" v-bind:fields="filterFields"/>
      <calendar-bar v-model="dateRange" @input="scheduleRefresh"/>
    </div>

    <b-overlay :show="loading" rounded="lg" opacity="0.45" spinner-variant="primary">

      <b-row class="kpi-row">
        <b-col xl="2" lg="4" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--success">
            <div class="kpi-card__icon">
              <feather-icon icon="DollarSignIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Total Deposits') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.totalDeposits) }}</div>
              <div v-if="delta.totalDeposits" class="kpi-card__delta"
                   :class="{ 'is-up': delta.totalDeposits.positive, 'is-down': !delta.totalDeposits.positive }">
                <feather-icon :icon="delta.totalDeposits.positive ? 'ArrowUpIcon' : 'ArrowDownIcon'" size="12"/>
                {{ delta.totalDeposits.label }} {{ $t('vs prev') }}
              </div>
              <div class="kpi-card__footer">
                <feather-icon icon="HashIcon" size="12" class="mr-25"/>
                {{ $t('Count') }}: {{ statistics.depositsCount }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl="2" lg="4" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--danger">
            <div class="kpi-card__icon">
              <feather-icon icon="ArrowDownCircleIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Total Withdrawals') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.totalWithdrawals) }}</div>
              <div v-if="delta.totalWithdrawals" class="kpi-card__delta"
                   :class="{ 'is-up': delta.totalWithdrawals.positive, 'is-down': !delta.totalWithdrawals.positive }">
                <feather-icon :icon="delta.totalWithdrawals.positive ? 'ArrowUpIcon' : 'ArrowDownIcon'" size="12"/>
                {{ delta.totalWithdrawals.label }} {{ $t('vs prev') }}
              </div>
              <div class="kpi-card__footer">
                <feather-icon icon="HashIcon" size="12" class="mr-25"/>
                {{ $t('Count') }}: {{ statistics.withdrawalsCount }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl="2" lg="4" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--primary">
            <div class="kpi-card__icon">
              <feather-icon icon="TrendingUpIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Net Deposits') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.netDeposits) }}</div>
              <div v-if="delta.netDeposits" class="kpi-card__delta"
                   :class="{ 'is-up': delta.netDeposits.positive, 'is-down': !delta.netDeposits.positive }">
                <feather-icon :icon="delta.netDeposits.positive ? 'ArrowUpIcon' : 'ArrowDownIcon'" size="12"/>
                {{ delta.netDeposits.label }} {{ $t('vs prev') }}
              </div>
              <div class="kpi-card__footer">
                <feather-icon icon="PercentIcon" size="12" class="mr-25"/>
                {{ $t('Withdrawal Rate') }}: {{ withdrawalRate }}%
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl="2" lg="4" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--primary">
            <div class="kpi-card__icon">
              <feather-icon icon="UserPlusIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('FTD') }}</div>
              <div class="kpi-card__value">{{ statistics.ftdCount }}</div>
              <div v-if="delta.ftdCount" class="kpi-card__delta"
                   :class="{ 'is-up': delta.ftdCount.positive, 'is-down': !delta.ftdCount.positive }">
                <feather-icon :icon="delta.ftdCount.positive ? 'ArrowUpIcon' : 'ArrowDownIcon'" size="12"/>
                {{ delta.ftdCount.label }} {{ $t('vs prev') }}
              </div>
              <div class="kpi-card__footer">
                <feather-icon icon="DollarSignIcon" size="12" class="mr-25"/>
                {{ convertToEURO(statistics.ftdAmount) }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl="2" lg="4" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--warning">
            <div class="kpi-card__icon">
              <feather-icon icon="RefreshCwIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('STD') }}</div>
              <div class="kpi-card__value">{{ statistics.stdCount }}</div>
              <div v-if="delta.stdCount" class="kpi-card__delta"
                   :class="{ 'is-up': delta.stdCount.positive, 'is-down': !delta.stdCount.positive }">
                <feather-icon :icon="delta.stdCount.positive ? 'ArrowUpIcon' : 'ArrowDownIcon'" size="12"/>
                {{ delta.stdCount.label }} {{ $t('vs prev') }}
              </div>
              <div class="kpi-card__footer">
                <feather-icon icon="DollarSignIcon" size="12" class="mr-25"/>
                {{ convertToEURO(statistics.stdAmount) }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl="2" lg="4" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--pending">
            <div class="kpi-card__icon">
              <feather-icon icon="ClockIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Pending Approvals') }}</div>
              <div class="kpi-card__value">{{ statistics.pendingApprovalsCount }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="AlertCircleIcon" size="12" class="mr-25"/>
                {{ $t('awaiting action') }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>
      </b-row>

      <b-card no-body class="mt-1">
        <b-card-header class="pb-50">
          <h5 class="mb-0">{{ $t('Deposits') }}</h5>
        </b-card-header>
        <b-card-body>
          <monetaries-table v-bind:filters="depositsFilters" :has-selection="false" trans-type="deposits"/>
        </b-card-body>
      </b-card>

      <b-card no-body class="mt-1">
        <b-card-header class="pb-50">
          <h5 class="mb-0">{{ $t('Withdrawals') }}</h5>
        </b-card-header>
        <b-card-body>
          <monetaries-table v-bind:filters="withdrawalsFilters" :has-selection="false" trans-type="withdrawals"/>
        </b-card-body>
      </b-card>

      <b-card no-body class="mt-1">
        <b-card-header class="pb-50">
          <h5 class="mb-0">{{ $t('Deposits & Withdrawals over time') }}</h5>
        </b-card-header>
        <b-card-body>
          <monetary-time-series-chart :points="statistics.series" :loading="loading" :is-dark-skin="isDarkSkin"/>
        </b-card-body>
      </b-card>

    </b-overlay>
  </div>
</template>

<script>
import CalendarBar from '@/components/widget/CalendarBar';
import mixinBase from '@/components/mixin/mixinBase';
import { mapActions, mapGetters } from 'vuex';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import FilterCard from '@/components/widget/FilterCard';
import MonetariesTable from '@/components/tables/MonetariesTable';
import MonetaryTimeSeriesChart from '@/components/chart/MonetaryTimeSeriesChart';
import MonetaryDashboardSearchRequest from '@/model/Dashboard/MonetaryDashboardSearchRequest';
import MonetaryDashboardFilterFields from '@/model/filter/MonetaryDashboardFilterFields';

const REFRESH_DEBOUNCE_MS = 250;

export default {
  name: 'MonetaryDashboard',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],

  components: {
    CalendarBar,
    FilterCard,
    MonetariesTable,
    MonetaryTimeSeriesChart,
  },

  data: () => ({
    filter: MonetaryDashboardSearchRequest(),
    filterFields: [],
    dateRange: { from: null, until: null },
    loading: false,
    inflight: false,
    pendingFetch: false,
    refreshTimer: null,
    statistics: {
      totalDeposits: 0,
      depositsCount: 0,
      totalWithdrawals: 0,
      withdrawalsCount: 0,
      netDeposits: 0,
      ftdCount: 0,
      ftdAmount: 0,
      stdCount: 0,
      stdAmount: 0,
      pendingApprovalsCount: 0,
      series: [],
      previous: null,
    },
  }),
  watch: {
    filter: {
      handler: 'scheduleRefresh',
      deep: true,
    },
  },
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    viewKey() {
      return this.$route.name || 'monetary-dashboard';
    },
    withdrawalRate() {
      const deposits = this.statistics.totalDeposits || 0;
      const withdrawals = this.statistics.totalWithdrawals || 0;
      if (!deposits) return '0.00';
      return (withdrawals / deposits * -100).toFixed(2);
    },
    depositsFilters() {
      return {
        type: 'DEPOSIT',
        orgIds: this.filter.orgIds,
        businessUnit: this.filter.buIds,
        userIds: this.filter.userIds,
        registrationTime: {
          start: this.$moment(this.dateRange.from).toServerDateTimeFormat(),
          end: this.$moment(this.dateRange.until).toServerDateTimeFormat()
        },
        realType: this.filter.realOnly === false ? null : ['REAL'],
        status: ['APPROVED'],
        action: ['CREDIT_LINE'],
        // Match the KPI aggregation, which always excludes test transactions
        // (isTest=false). Without this the table counts test deposits and
        // disagrees with the card count above.
        isTest: false,
      };
    },
    withdrawalsFilters() {
      return {
        type: 'WITHDRAWAL',
        orgIds: this.filter.orgIds,
        businessUnit: this.filter.buIds,
        userIds: this.filter.userIds,
        registrationTime: {
          start: this.$moment(this.dateRange.from).toServerDateTimeFormat(),
          end: this.$moment(this.dateRange.until).toServerDateTimeFormat()
        },
        realType: this.filter.realOnly === false ? null : ['REAL'],
        status: ['APPROVED'],
        action: ['CREDIT_LINE'],
        // Match the KPI aggregation, which always excludes test transactions.
        isTest: false,
      };
    },
    delta() {
      const prev = this.statistics.previous;
      if (!prev) return {};
      return {
        totalDeposits: this.computeDelta(this.statistics.totalDeposits, prev.totalDeposits),
        totalWithdrawals: this.computeDelta(this.statistics.totalWithdrawals, prev.totalWithdrawals),
        netDeposits: this.computeDelta(this.statistics.netDeposits, prev.netDeposits),
        ftdCount: this.computeDelta(this.statistics.ftdCount, prev.ftdCount),
        stdCount: this.computeDelta(this.statistics.stdCount, prev.stdCount),
      };
    },
  },
  created() {
    this.filterFields = MonetaryDashboardFilterFields();
    this.dateRange= {
      from: this.$moment().startOf('week').format('YYYY-MM-DD'),
      until: this.$moment().endOf('week').format('YYYY-MM-DD'),
    }
  },
  mounted() {
    this.$root.$on('dashboard::refresh', this.scheduleRefresh);
  },
  beforeDestroy() {
    this.$root.$off('dashboard::refresh', this.scheduleRefresh);
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  },
  methods: {
    ...mapActions('dashboard', ['getMonetaryDashboard']),
    scheduleRefresh() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), REFRESH_DEBOUNCE_MS);
    },
    refresh() {
      if (!this.dateRange.from) return;
      if (this.inflight) {
        this.pendingFetch = true;
        return;
      }
      this.doFetch();
    },
    doFetch() {
      const payload = {
        ...this.filter,
        range: { from: this.dateRange.from, until: this.dateRange.until },
      };
      this.inflight = true;
      this.loading = true;
      this.getMonetaryDashboard(payload)
        .then(
          data => this.setData(data),
          err => this.showServerErrorNotification('getMonetaryDashboard', 'dashboard', err)
        )
        .finally(() => {
          this.inflight = false;
          this.loading = false;
          if (this.pendingFetch) {
            this.pendingFetch = false;
            this.doFetch();
          }
        });
    },
    setData(data) {
      if (!data) return;
      this.statistics.totalDeposits = data.totalDeposits || 0;
      this.statistics.depositsCount = data.depositsCount || 0;
      this.statistics.totalWithdrawals = data.totalWithdrawals || 0;
      this.statistics.withdrawalsCount = data.withdrawalsCount || 0;
      this.statistics.netDeposits = data.netDeposits || 0;
      this.statistics.ftdCount = data.ftdCount || 0;
      this.statistics.ftdAmount = data.ftdAmount || 0;
      this.statistics.stdCount = data.stdCount || 0;
      this.statistics.stdAmount = data.stdAmount || 0;
      this.statistics.pendingApprovalsCount = data.pendingApprovalsCount || 0;
      this.statistics.series = data.series || [];
      this.statistics.previous = data.previous || null;
    },
    computeDelta(current, previous) {
      const cur = Number(current) || 0;
      const prev = Number(previous) || 0;
      if (prev === 0) {
        if (cur === 0) return null;
        return { label: '—', positive: cur >= 0 };
      }
      const pct = ((cur - prev) / Math.abs(prev)) * 100;
      const positive = pct >= 0;
      const sign = positive ? '+' : '';
      return { label: `${sign}${pct.toFixed(1)}%`, positive };
    },
  },
};
</script>

<style scoped lang="scss">
.monetary-dashboard {
  position: relative;

  &__toolbar {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
}

.kpi-row {
  margin-top: 0.5rem;
}

.kpi-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.35rem;
  border-radius: 18px;
  color: #fff;
  overflow: hidden;
  box-shadow: 0 12px 28px -14px rgba(20, 24, 50, 0.28),
              0 2px 4px rgba(20, 24, 50, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  min-height: 118px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 40px -16px rgba(20, 24, 50, 0.35),
                0 4px 8px rgba(20, 24, 50, 0.08);
  }

  &__icon {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.22);
    backdrop-filter: blur(6px);
    flex-shrink: 0;
  }

  &__body {
    position: relative;
    z-index: 2;
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 0.82rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.86;
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value {
    font-size: 1.55rem;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__footer {
    margin-top: 0.45rem;
    font-size: 0.76rem;
    opacity: 0.9;
    display: flex;
    align-items: center;
  }

  &__delta {
    margin-top: 0.35rem;
    font-size: 0.72rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.18);

    &.is-up { color: #d6ffe8; }
    &.is-down { color: #ffe0e0; }
  }

  &__glow {
    position: absolute;
    top: -40%;
    right: -20%;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 70%);
    pointer-events: none;
    z-index: 1;
  }

  &--primary {
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%);
  }

  &--success {
    background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%);
  }

  &--warning {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%);
  }

  &--danger {
    background: linear-gradient(135deg, #fb7185 0%, #ef4444 55%, #c1272d 100%);
  }

  &--pending {
    background: linear-gradient(135deg, #a78bfa 0%, #7c3aed 55%, #5b21b6 100%);
  }
}

@media (max-width: 576px) {
  .kpi-card {
    min-height: auto;
    padding: 1rem;
    &__value { font-size: 1.3rem; }
  }
}
</style>
